import { Canvas, Section } from 'datocms-react-ui'
import { Button } from 'datocms-react-ui'
import { IPluginFieldType, IPluginState } from './types'
import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { RenderPageCtx } from 'datocms-plugin-sdk'
import { LANGS } from './utils/langs'
import {
  ApiError,
  Client,
  SimpleSchemaTypes,
  buildClient,
} from '@datocms/cma-client-browser'
import {
  getTranslatableFields,
  isFieldValueEmpty,
  parsePluginField,
} from './helpers'

interface ICmsModel {
  name: string
  id: string
  modular_block: boolean
}

interface IModelFields {
  missingTranslations?: IPluginFieldType
  missingTranslationsState?: IPluginState
  translatableAttributes?: { [key: string]: IPluginFieldType }
  recordId: string
}

interface IModelPluginFields {
  jsonFieldKey: string
  textFieldKey: string
}

type AdminPropTypes = {
  ctx: RenderPageCtx
}

const initializeLangInRecord = (
  fields: { [key: string]: any },
  mainLanguage: string,
  pluginFields: IModelPluginFields,
  allLanguages: string[]
) => {
  // New JSON plugin field that will be set in the record
  const newMissingTranslationsJsonState =
    parsePluginField(fields[pluginFields.jsonFieldKey]) || {}

  allLanguages.forEach((lang) => {
    if (!(lang in newMissingTranslationsJsonState)) {
      newMissingTranslationsJsonState[lang] = {}
    }
  })

  const updateRequest: any = {}

  const excludedFields = [pluginFields.jsonFieldKey, pluginFields.textFieldKey]

  // Get only the fields that are translatable and not part of the plugin
  const translatableFieldKeys = getTranslatableFields(
    fields,
    excludedFields,
    mainLanguage
  )

  // Go trough all fields
  for (const fieldId of translatableFieldKeys) {
    const fieldValue = fields[fieldId]

    for (const lang of allLanguages) {
      if (lang === mainLanguage) {
        newMissingTranslationsJsonState[lang][fieldId] = false
      } else {
        if (!(lang in fieldValue) || isFieldValueEmpty(fieldValue[lang])) {
          // Add field in request if not there already
          if (!(fieldId in updateRequest)) {
            updateRequest[fieldId] = fieldValue
          }
          // Copy content from main language to current language
          updateRequest[fieldId][lang] = fieldValue[mainLanguage]
          newMissingTranslationsJsonState[lang][fieldId] = !isFieldValueEmpty(
            fieldValue[mainLanguage]
          )

          // Special case for modular fields
          // Remove ID if such field is found
          if (
            Array.isArray(updateRequest[fieldId][lang]) &&
            updateRequest[fieldId][lang].length &&
            updateRequest[fieldId][lang][0].type === 'item' &&
            updateRequest[fieldId][lang][0].id
          ) {
            updateRequest[fieldId][lang] = [...updateRequest[fieldId][lang]]
            for (const modelEntry in updateRequest[fieldId][lang]) {
              delete updateRequest[fieldId][lang][modelEntry]['id']
            }
          }
        } else {
          if (
            !Object.prototype.hasOwnProperty.call(
              newMissingTranslationsJsonState[lang],
              fieldId
            )
          ) {
            newMissingTranslationsJsonState[lang][fieldId] = !isFieldValueEmpty(
              fieldValue[mainLanguage]
            )
          }
        }
      }
    }
  }

  updateRequest[pluginFields.textFieldKey] =
    fields[pluginFields.textFieldKey] || {}

  updateRequest[pluginFields.jsonFieldKey] = newMissingTranslationsJsonState

  for (const key of allLanguages) {
    updateRequest[pluginFields.textFieldKey][key] = Object.values(
      newMissingTranslationsJsonState[key]
    ).includes(true)
      ? 'YES'
      : 'NO'
    updateRequest[pluginFields.jsonFieldKey][key] = JSON.stringify(
      newMissingTranslationsJsonState[key],
      null,
      2
    )
  }
  // Set for current language
  updateRequest[pluginFields.textFieldKey][mainLanguage] = 'NO'

  return updateRequest
}

type IInitializationsInProgress = {
  [key: string]: number
}

export function TranslationsAdmin({ ctx }: AdminPropTypes) {
  // @TODO: The list of admin roles should be specified in the plugin configuration
  const isAdmin =
    ctx.currentRole.attributes.name === 'Admin' ||
    ctx.currentRole.attributes.name === 'Account role' ||
    ctx.currentRole.attributes.can_edit_environment

  const [models, setModels] = useState<ICmsModel[]>([])
  const [modelMissingTranslations, setModelMissingTranslations] = useState<
    Map<string, IModelFields[]>
  >(new Map())
  const [modelPluginFields, setModelPluginFields] = useState<
    Map<string, IModelPluginFields>
  >(new Map())
  const [initializationsInProgress, setInitializationsInProgress] =
    useState<IInitializationsInProgress>({})
  const [currentlyInstalling, setCurrentlyInstalling] = useState<boolean>(false)

  const allLanguages = ctx.site.attributes.locales
  const languageLabels = LANGS

  const [refresh, setRefresh] = useState<boolean>(true)
  const [client, setClient] = useState<Client | undefined>()
  const mainLanguage = allLanguages[0]

  useEffect(() => {
    setClient(
      buildClient({
        apiToken: ctx.currentUserAccessToken || '',
        environment: ctx.environment,
      })
    )
  }, [])

  // Load all models
  useEffect(() => {
    // eslint-disable-next-line no-extra-semi
    ;(async () => {
      if (!refresh || !client) return

      const result = await client.itemTypes.list()
      const newModels = result
        .map(({ id, name, modular_block }) => ({ id, name, modular_block }))
        .filter((e: ICmsModel) => !e.modular_block)

      setModels(newModels)
      setRefresh(false)
    })()
  }, [refresh, client])

  useEffect(() => {
    // eslint-disable-next-line no-extra-semi
    ;(async () => {
      if (!client) {
        return
      }
      const finalMap = new Map()
      const modelPluginMap = new Map()
      const modelIds = models.map((model) => model.id).join(',')
      const modelItemsData: SimpleSchemaTypes.Item[] = []
      let offset = 0
      let modelItems: SimpleSchemaTypes.ItemInstancesTargetSchema | null = null

      // Handle retrieving of data when items are more than 500
      do {
        modelItems = await client.items.list({
          filter: {
            type: modelIds,
          },
          page: {
            offset,
            limit: 500,
          },
          version: 'current',
        })

        if (modelItems && modelItems && Array.isArray(modelItems)) {
          modelItemsData.push(...modelItems)
        }
        offset += 500
      } while (modelItems && modelItemsData.length > 490 && modelItems.length)

      await Promise.all(
        models.map(async (model) => {
          const modelFields = await client.fields.list(model.id)
          if (
            modelFields &&
            Array.isArray(modelFields) &&
            modelItemsData &&
            Array.isArray(modelItemsData)
          ) {
            const currentModelItems = modelItemsData.filter(
              (modelItem) => modelItem.item_type.id === model.id
            )

            const jsonPluginField = modelFields.find(
              (element) =>
                element.appearance.field_extension ===
                'missingTranslationsStore'
            )
            const textPluginField = modelFields.find(
              (element) =>
                element.appearance.field_extension ===
                'missingTranslationsField'
            )

            const excludedFields = [
              jsonPluginField?.api_key || '',
              textPluginField?.api_key || '',
            ]

            // Get all relevant information for each record in the model
            const modelRecords: IModelFields[] = currentModelItems.map(
              (record) => ({
                missingTranslations: textPluginField
                  ? (record[textPluginField.api_key] as IPluginFieldType)
                  : undefined,
                missingTranslationsState: jsonPluginField
                  ? parsePluginField(
                      record[jsonPluginField.api_key] as IPluginFieldType
                    )
                  : undefined,
                translatableAttributes: Object.keys(record)
                  .filter(
                    (key) =>
                      !excludedFields.includes(key) &&
                      record[key] instanceof Object &&
                      Object.keys(record[key] as any).includes(mainLanguage)
                  )
                  .reduce((acc, el) => {
                    acc[el] = record[el]
                    return acc
                  }, {} as any),
                recordId: record.id,
              })
            )

            finalMap.set(model.id, modelRecords)
            if (jsonPluginField && textPluginField) {
              modelPluginMap.set(model.id, {
                jsonFieldKey: jsonPluginField.api_key,
                textFieldKey: textPluginField.api_key,
              })
            }
          }
        })
      )
      setModelMissingTranslations(finalMap)
      setModelPluginFields(modelPluginMap)
      setInitializationsInProgress({})
      setCurrentlyInstalling(false)
    })()
  }, [models])

  const setupModelFields = (modelId: string) => {
    // eslint-disable-next-line no-extra-semi
    ;(async () => {
      if (!client) {
        return
      }
      if (currentlyInstalling) {
        return
      }
      setCurrentlyInstalling(true)

      try {
        await client.fields.rawCreate(modelId, {
          data: {
            type: 'field',
            attributes: {
              label: 'Missing Translations State',
              field_type: 'string',
              api_key: 'missing_translations_state',
              localized: true,
              appearance: {
                editor: ctx.plugin.id,
                parameters: {},
                addons: [],
                field_extension: 'missingTranslationsField',
              },
              hint: 'This field will be used to show missing translations',
            },
          },
        })
        await client.fields.rawCreate(modelId, {
          data: {
            type: 'field',
            attributes: {
              label: 'Missing Translations Json',
              field_type: 'json',
              api_key: 'missing_translations_json',
              localized: true,
              appearance: {
                editor: ctx.plugin.id,
                parameters: {},
                addons: [],
                field_extension: 'missingTranslationsStore',
              },
              hint: 'This field will be ued to store fields that need to be translated',
            },
          },
        })
      } catch (error) {
        if (error instanceof ApiError) {
          if (
            error.errors.some(
              (error) =>
                error.attributes.details.code === 'VALIDATION_UNIQUENESS'
            )
          ) {
            alert(
              'The translation fields already exist. Please connect them to the plugin manually from the models page'
            )
          } else {
            alert(
              'Unknown error code -- ' +
                (error.errors[0].attributes.details.code ??
                  error.errors[0].type)
            )
          }
        } else {
          alert('Unknown error while creating fields. Try again later')
        }
      } finally {
        setTimeout(() => {
          setCurrentlyInstalling(true)
          setRefresh(true)
        }, 3000)
      }
    })()
  }

  const getStatusOfModel = (model: ICmsModel) => {
    const pluginFields = modelPluginFields.get(model.id)
    const modelRecords = modelMissingTranslations.get(model.id) || []

    // Get records that are problematic
    const problematicRecords = modelRecords.filter((record) => {
      return (
        !record.missingTranslations ||
        (record.missingTranslations &&
          Object.keys(record.missingTranslations).length <
            allLanguages.length) ||
        record.missingTranslations?.[mainLanguage] !== 'NO' ||
        (record.translatableAttributes &&
          Object.keys(record.translatableAttributes).some((recordFieldKey) => {
            const recordField = record.translatableAttributes?.[recordFieldKey]
            const isInvalid =
              (isFieldValueEmpty(recordField?.[mainLanguage]) &&
                allLanguages.some(
                  (lang) =>
                    isFieldValueEmpty(recordField?.[lang]) &&
                    record.missingTranslationsState?.[lang]?.[recordFieldKey]
                )) ||
              !(
                recordFieldKey in
                (record?.missingTranslationsState?.[mainLanguage] || {})
              )
            return isInvalid
          })) ||
        (record.translatableAttributes &&
          Object.keys(record.translatableAttributes).some((recordFieldKey) => {
            const recordField = record.translatableAttributes?.[recordFieldKey]
            const isInvalid =
              !isFieldValueEmpty(recordField?.[mainLanguage]) &&
              allLanguages.some((lang) =>
                isFieldValueEmpty(recordField?.[lang])
              )
            return isInvalid
          }))
      )
    })

    const setupRecord = async () => {
      if (!pluginFields || !client) {
        return
      }
      const modelId = model.id
      const newInProgressState = { ...initializationsInProgress }
      if (!newInProgressState[modelId]) {
        newInProgressState[modelId] = 0
      }

      for (const record of problematicRecords) {
        const recordCurrentState = await client.items.rawFind(record.recordId, {
          nested: true,
        })
        if (!recordCurrentState) {
          return
        }

        const attributes = initializeLangInRecord(
          recordCurrentState.data.attributes,
          mainLanguage,
          pluginFields,
          allLanguages
        )

        await client.items.rawUpdate(record.recordId, {
          data: {
            type: recordCurrentState.data.type,
            id: recordCurrentState.data.id,
            attributes,
          },
        })

        newInProgressState[modelId] += 1
        setInitializationsInProgress({ ...newInProgressState })
      }
      setRefresh(true)
    }
    return (
      <tr key={'model-row-' + model.id}>
        <td>{model.name}</td>
        <td>
          {!pluginFields ? (
            <>
              <Orange>Plugin to be added</Orange>
              {isAdmin && (
                <Button
                  buttonSize="xxs"
                  style={{ marginLeft: '14px' }}
                  onClick={() => setupModelFields(model.id)}
                  disabled={currentlyInstalling}
                >
                  Install
                </Button>
              )}
            </>
          ) : (
            problematicRecords.length > 0 &&
            isAdmin && (
              <>
                <Orange>There are records that need to be updated</Orange>
                <Button
                  buttonSize="xxs"
                  style={{ marginLeft: '14px' }}
                  disabled={!!Object.keys(initializationsInProgress).length}
                  onClick={() => setupRecord()}
                >
                  Update ({' '}
                  {problematicRecords.length -
                    (initializationsInProgress[model.id] || 0)}{' '}
                  )
                </Button>
              </>
            )
          )}
        </td>
        {allLanguages.map((lang) => (
          <td key={'td-lang-' + lang}>
            {pluginFields && getStatusOfModelAndLang(model.id, lang)}
          </td>
        ))}
      </tr>
    )
  }
  const getStatusOfModelAndLang = (modelId: string, lang: string) => {
    const modelRecords = modelMissingTranslations.get(modelId) || []

    const notTranslatedFields = modelRecords.filter((record) => {
      if (!record.missingTranslations) {
        return false
      }
      // Check recrods for which the plugin has no value or Check records for which the plugin knows that a translation is missing
      return (
        !Object.keys(record.missingTranslations).includes(lang) ||
        record.missingTranslations[lang] !== 'NO'
      )
    }).length

    if (notTranslatedFields === 0) {
      return <Green>Done</Green>
    }

    return (
      <>
        {!!notTranslatedFields && (
          <OneLine>
            <Red>Not translated: {notTranslatedFields}</Red>
          </OneLine>
        )}
      </>
    )
  }

  return (
    <Canvas ctx={ctx}>
      <AdminContent>
        <Section title="Models">
          <StyledTable>
            <tr>
              <th>NAME</th>
              <th>CONTROLS</th>
              {allLanguages.map((lang) => (
                <th key={'th-lang-' + lang}>{languageLabels[lang]}</th>
              ))}
            </tr>
            {models
              .filter((model) => isAdmin || modelPluginFields.has(model.id))
              .map((model) =>
                modelMissingTranslations.get(model.id) ? (
                  getStatusOfModel(model)
                ) : (
                  <tr key={'missing-item-key' + model.id}>
                    <td colSpan={1000}>Fetching data...</td>
                  </tr>
                )
              )}
          </StyledTable>
        </Section>
      </AdminContent>
    </Canvas>
  )
}

const AdminContent = styled.div`
  max-width: 900px;
  margin: 80px auto 80px auto;
`

const StyledTable = styled.table`
  border: 1px solid #ccc;
  border-collapse: collapse;
  width: 100%;
  > tr > td,
  > tr > th {
    padding: 8px;
    text-align: left;
    min-width: 60px;
    vertical-align: initial;
    border: 1px solid #eee;
  }
`

const Green = styled.div`
  color: green;
`

const Red = styled.div`
  color: red;
`

const Orange = styled.div`
  color: orange;
`

const OneLine = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`
